import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { OpenAPI } from "@/client/core/OpenAPI"

// Types mirror backend MergeProposalPublic / MergeProposalsListPublic.
// Hand-written (following useTopicProposals.ts) until the generated client is
// refreshed via ./scripts/generate-client.sh.
export interface MergeProposalPublic {
  id: string
  source_topic_id: string | null
  source_topic_name: string | null
  destination_topic_id: string | null
  destination_topic_name: string | null
  score: number | null
  reason: string | null
  status: string
  moved_associations: number | null
}

export interface MergeProposalsResponse {
  data: MergeProposalPublic[]
  count: number
}

function apiBaseUrl(): string {
  const base = (OpenAPI.BASE || import.meta.env.VITE_API_URL || "").replace(
    /\/$/,
    "",
  )
  if (!base) {
    throw new Error(
      "API base URL is not configured. Set VITE_API_URL to your backend origin.",
    )
  }
  return base
}

function authHeaders(): Record<string, string> {
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("access_token")}`,
  }
}

async function getMergeProposals(
  status: string,
): Promise<MergeProposalsResponse> {
  const response = await fetch(
    `${apiBaseUrl()}/api/v1/ai/topic-merge/proposals?status=${status}`,
    { headers: authHeaders() },
  )
  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.detail || "Failed to load merge proposals")
  }
  return response.json()
}

async function postAction(proposalId: string, action: string): Promise<void> {
  const response = await fetch(
    `${apiBaseUrl()}/api/v1/ai/topic-merge/proposals/${proposalId}/${action}`,
    { method: "POST", headers: authHeaders() },
  )
  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.detail || `Failed to ${action} merge proposal`)
  }
}

export function useTopicMergeProposals(status = "proposed") {
  return useQuery({
    queryKey: ["topic-merge-proposals", status],
    queryFn: () => getMergeProposals(status),
  })
}

export function useApplyTopicMergeProposal() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => postAction(id, "apply"),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["topic-merge-proposals"] }),
  })
}

export function useDismissTopicMergeProposal() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => postAction(id, "dismiss"),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["topic-merge-proposals"] }),
  })
}
